import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import axios from "axios";
import { API_BASE, setAuthToken, removeAuthToken, setRefreshHandler } from "../utils/api";
import type { RegisterUserInput, RegisterDoctorInput } from "../types/user";

// ---------------------------------------------------------------------------
// Cognito Pool Configuration
// ---------------------------------------------------------------------------
const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || "",
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || "",
};

const userPool = new CognitoUserPool(poolData);

/**
 * Sign-in identifiers to try, in order.
 *
 * Cognito resolves the email alias case-sensitively and that is immutable once
 * a pool exists, so neither the typed form nor the lowercase form works on its
 * own:
 *
 *  - Accounts registered before addresses were normalised hold a mixed-case
 *    email (e.g. "Taimur.if@gmail.com"). Lowercasing locks those doctors out.
 *  - Accounts registered since are lowercase, so a doctor who capitalises their
 *    own address needs the lowercase form.
 *
 * Trying as-typed first and falling back to lowercase covers both. The typed
 * form goes first so an existing mixed-case account authenticates on the first
 * attempt, with no extra round trip and no behaviour change for them.
 */
const usernameCandidates = (username: string): string[] => {
  const typed = username.trim();
  const lowered = typed.toLowerCase();
  return lowered === typed ? [typed] : [typed, lowered];
};

/** Cognito's "no such account" signal, the only error worth a second attempt. */
const isUserNotFound = (err: any): boolean =>
  err?.code === "UserNotFoundException" ||
  err?.name === "UserNotFoundException" ||
  /user does not exist/i.test(err?.message || "");

// We keep a reference to the current CognitoUser so MFA confirm can access it
let currentCognitoUser: CognitoUser | null = null;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type LoginResult =
  | { status: "success"; token: string; user: any }
  | { status: "mfa_required"; mfaType: "SMS_MFA" | "SOFTWARE_TOKEN_MFA"; destination?: string };

// ---------------------------------------------------------------------------
// Auth Service
// ---------------------------------------------------------------------------
const AuthService = {
  /**
   * Step 1: Initiate login.
   * Returns either a success (with the JWT) or an MFA challenge.
   */
  async login(username: string, password: string): Promise<LoginResult> {
    const candidates = usernameCandidates(username);
    for (let i = 0; i < candidates.length; i += 1) {
      const isLast = i === candidates.length - 1;
      try {
        return await AuthService._loginAs(candidates[i], password);
      } catch (err: any) {
        // Only an unknown account is worth retrying under different casing —
        // a wrong password must surface immediately, not be masked by a second
        // attempt that reports "user does not exist".
        if (isLast || !isUserNotFound(err)) throw err;
      }
    }
    throw new Error("Login failed.");
  },

  /** One authentication attempt against a single spelling of the username. */
  _loginAs(username: string, password: string): Promise<LoginResult> {
    console.log("[AuthService] login() called with:", username);
    console.log("[AuthService] Pool config:", poolData);

    return new Promise((resolve, reject) => {
      const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      currentCognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      console.log("[AuthService] Calling authenticateUser...");

      currentCognitoUser.authenticateUser(authDetails, {
        onSuccess: (session: CognitoUserSession) => {
          console.log("[AuthService] ✅ onSuccess fired!");
          const idToken = session.getIdToken().getJwtToken();
          console.log("[AuthService] ID Token (first 50 chars):", idToken.substring(0, 50));
          setAuthToken(idToken);
          resolve({ status: "success", token: idToken, user: null });
        },

        onFailure: (err) => {
          console.error("[AuthService] ❌ onFailure fired:", err);
          // Rejected as an Error rather than a bare string so login() can see
          // the Cognito code and decide whether another spelling is worth a
          // try. `message` is unchanged, and useAuth already accepts either.
          const failure: any = new Error(err?.message || "Login failed.");
          failure.code = (err as any)?.code ?? (err as any)?.name;
          reject(failure);
        },

        // MFA TEMPORARILY DISABLED — re-enable once SNS sandbox exit is approved
        // SDK requires these callbacks to be functions even when MFA is off at pool level
        mfaRequired: () => {
          reject("MFA is temporarily disabled. Please contact admin.");
        },
        totpRequired: () => {
          reject("MFA is temporarily disabled. Please contact admin.");
        },

        // Challenge: User needs to setup MFA (happens on first login if enforced)
        mfaSetup: (_challengeName, _challengeParameters) => {
          console.log("[AuthService] ⚠️ mfaSetup fired:", _challengeName);
          reject("MFA Setup required. Please use the mobile app or contact admin to register your MFA device.");
        },

        // First-time login: doctor must set a new password
        newPasswordRequired: (_userAttributes, _requiredAttributes) => {
          console.log("[AuthService] 🔑 newPasswordRequired fired");
          reject("Initial password change required. Contact admin or use the CLI to set a permanent password.");
        },
      });
    });
  },

  /**
   * Step 2: Confirm MFA code.
   * Called after login returns "mfa_required".
   */
  confirmMfa(code: string, mfaType: "SMS_MFA" | "SOFTWARE_TOKEN_MFA"): Promise<LoginResult> {
    return new Promise((resolve, reject) => {
      if (!currentCognitoUser) {
        reject("No active login session. Please start login again.");
        return;
      }

      currentCognitoUser.sendMFACode(
        code,
        {
          onSuccess: (session: CognitoUserSession) => {
            const idToken = session.getIdToken().getJwtToken();
            setAuthToken(idToken);
            resolve({ status: "success", token: idToken, user: null });
          },
          onFailure: (err) => {
            reject(err.message || "Invalid MFA code.");
          },
        },
        mfaType
      );
    });
  },

  /**
   * Register a new user via the existing Django backend.
   */
  async register(data: RegisterUserInput | RegisterDoctorInput) {
    try {
      const response = await axios.post(`${API_BASE}/users/register/`, data);
      return response.data;
    } catch (error: any) {
      throw error.response
        ? error.response.data
        : "Registration failed. Please try again.";
    }
  },

  /**
   * Silently refresh the Cognito session using the stored refresh token.
   * getSession() auto-refreshes if the access token is expired.
   */
  refreshToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      const user = userPool.getCurrentUser();
      if (!user) {
        reject("No current user");
        return;
      }
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err?.message || "Session refresh failed");
          return;
        }
        const newToken = session.getIdToken().getJwtToken();
        setAuthToken(newToken);
        resolve(newToken);
      });
    });
  },

  /**
   * Forgot password — step 1: request a reset code.
   * Cognito emails a verification code to the user's registered email.
   */
  async forgotPassword(username: string): Promise<void> {
    const candidates = usernameCandidates(username);
    for (let i = 0; i < candidates.length; i += 1) {
      const isLast = i === candidates.length - 1;
      try {
        return await AuthService._forgotPasswordAs(candidates[i]);
      } catch (err: any) {
        if (isLast || !isUserNotFound(err)) throw err;
      }
    }
  },

  /** One reset-code request against a single spelling of the username. */
  _forgotPasswordAs(username: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({ Username: username, Pool: userPool });
      user.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => {
          const failure: any = new Error(
            err?.message || "Could not send reset code.",
          );
          failure.code = (err as any)?.code ?? (err as any)?.name;
          reject(failure);
        },
      });
    });
  },

  /**
   * Forgot password — step 2: confirm the code and set a new password.
   */
  async confirmPassword(
    username: string,
    code: string,
    newPassword: string,
  ): Promise<void> {
    // Must resolve to the same account forgotPassword() sent the code to, so
    // it walks the same candidates in the same order.
    const candidates = usernameCandidates(username);
    for (let i = 0; i < candidates.length; i += 1) {
      const isLast = i === candidates.length - 1;
      try {
        return await AuthService._confirmPasswordAs(
          candidates[i],
          code,
          newPassword,
        );
      } catch (err: any) {
        if (isLast || !isUserNotFound(err)) throw err;
      }
    }
  },

  /** One reset confirmation against a single spelling of the username. */
  _confirmPasswordAs(
    username: string,
    code: string,
    newPassword: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const user = new CognitoUser({ Username: username, Pool: userPool });
      user.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => {
          const failure: any = new Error(
            err?.message || "Could not reset password.",
          );
          failure.code = (err as any)?.code ?? (err as any)?.name;
          reject(failure);
        },
      });
    });
  },

  /**
   * Logout: clear the local token and sign out from Cognito.
   */
  logout() {
    const user = userPool.getCurrentUser();
    if (user) user.signOut();
    removeAuthToken();
    localStorage.removeItem("user");
  },
};

// Register refresh handler with axios interceptor (avoids circular import)
setRefreshHandler(() => AuthService.refreshToken());

export default AuthService;
